import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Send, MessageCircle, Mic, Pause, Play, Square, Video, Brain, ArrowUp, Sparkles, X } from "lucide-react";
import ImagePreviewModal from "../ImagePreviewModal";
import EndChatConfirmModal from "./EndChatConfirmModal";
import { openDocument } from "./utils/openDocument";
import { formatBotMessage, formatTimestamp } from "./utils/formatMessage";
import { buildConversationSummary, normalizeSummaryMessages } from "./utils/buildConversationSummary";
import { authFetch } from "../../utils/auth";
import useZoomBridgeSocket from "./hooks/useZoomBridgeSocket";


// How long the transcript must be quiet before the one automatic summary is
// written.
// The summary is markdown with a known skeleton: four bold section titles,
// each holding bold **User** / **Copilot** speaker labels. formatBotMessage
// renders every one of those as an identical <strong> separated by <br><br>,
// which is where the empty space came from — nothing could be styled as a
// heading because nothing was marked as one, and the only spacing tool was a
// blank line.
//
// Promoted here rather than in formatMessage: that formatter is shared with
// every copilot answer, and "Summary" is a common enough bold line there that
// a generic rule would restyle messages this drawer has nothing to do with.
const SUMMARY_SECTIONS = [
  "Initial questions asked", "Summary", "Analysis so far",
  "Last step taken",
];

function summaryHtml(markdown) {
  let html = formatBotMessage(markdown || "");
  for (const title of SUMMARY_SECTIONS) {
    html = html.split(`<strong>${title}</strong>`).join(
      `<div class="d5-sum-h">${title}</div>`);
  }
  // "Chatbot" is the retired label: a summary generated before this rename
  // (already sitting in an open tab's aiSummary state, or a fallback string
  // returned by an old server process) still carries **Chatbot** speaker
  // blocks. Accepting both on read, while every producer now only ever
  // EMITS "Copilot", keeps that stale text from rendering as a raw
  // <strong> tag instead of a styled speaker block.
  for (const [raw, label] of [["User", "User"], ["Chatbot", "Copilot"], ["Copilot", "Copilot"]]) {
    html = html.split(`<strong>${raw}</strong>`).join(
      `<div class="d5-sum-who">${label}</div>`);
  }
  // Both blocks carry their own margins; the <br>s that used to do the spacing
  // now stack on top of them, which is the hole in the middle of the drawer.
  html = html.replace(/(<br>\s*)+(<div class="d5-sum-(?:h|who)">)/g, "$2");
  html = html.replace(/(<\/div>)(\s*<br>)+/g, "$1");
  return html;
}

const SUMMARY_SETTLE_MS = 1500;

const CustomerChatPanel = forwardRef(({ session: sessionProp, embedded = false, socket, isConnected = false, agentName = "TSE Agent", aiAgentName = "AI Agent", customerUsername = "Customer", liveChatEnded = false, liveChatEndedBy = null, onEndLiveChat, onCollapse, enableZoomBridge = false, sessionId, customerSessionId = "", onRequestDiagnosis, onJoinCall, contextCount = 0, contextChars = 0, contextOpen = false, onToggleContext, sentiment = null, prevSentiment = null }, ref) => {
  const [messages, setMessages] = useState([]);
  // null, or { state: "reconnecting", secondsLeft }. Drives the header while
  // the customer is away — the engineer needs to know BEFORE they type a
  // paragraph into a chat nobody is reading.
  const [peerState, setPeerState] = useState(null);
  const activeSession = sessionProp;
  const chatEnded = liveChatEnded || activeSession?.liveChatEnded;
  const canChat = Boolean(activeSession?.escalationActive) && !chatEnded;
  // Whether the COMPOSER accepts typing, which is a weaker condition than
  // canChat. canChat needs the escalation flag, and that flag only arrives from
  // a successful tse:bind_escalation — so a refused or missed bind left the box
  // both disabled and readOnly with no way to reach it, even though the
  // conversation was on screen and `deliver` had a target to send to. What
  // actually gates sending is having a customer to send to.
  const customerTarget = customerSessionId || activeSession?.user_sid || "";
  const canType = Boolean(customerTarget) && !chatEnded;
  const chatEndedStatusAppended = useRef(false);

  // AI Conversation Summary — the engineer's own read of the chat, in its own
  // drawer. Deliberately NOT the fact sheet the send arrow builds: that one is
  // condensed by the model and handed to the Copilot as context, this one is
  // the narrative the engineer reads on taking over, and nothing else consumes
  // it.
  //
  // Written by Haiku (POST /api/copilot/summarize-conversation), which is told
  // to return the four fixed sections — Initial questions asked, Summary,
  // Analysis so far, Last step taken — so an engineer walking into a live
  // escalation reads the same shape every time.
  //
  // ONE call per drawer-open, never on a timer: cached in `aiSummary` and only
  // dropped when the transcript actually moves on (see the effect below), so
  // opening and closing the drawer costs nothing.
  //
  // buildConversationSummary() is the fallback, not dead code. It renders the
  // SAME four sections from regex over the transcript, so a failed or
  // unreachable endpoint downgrades the prose, never the shape — the handoff
  // panel has to work when the LLM does not.
  const [aiSummary, setAiSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);

  const capName = (name) => {
    if (!name) return "";
    const s = String(name).trim();
    if (s.length > 10) {
      return s.slice(0, 10) + "…";
    }
    return s;
  };

  const messageReceipt = (m) => {
    const sender = String(m.sender || "").toLowerCase();
    const time = formatTimestamp(m.timestamp);
    if (sender === "user" || sender === "customer") {
      return `${capName(customerUsername || "Customer")} ${time}`;
    }
    if (sender === "agent") {
      const name = capName(agentName || m.senderName || localStorage.getItem("TseAgentName") || "Agent");
      return `${name} ${time}`;
    }
    if (sender === "bot") {
      // The panel prop is the persona the customer was actually shown. A
      // sender_name baked into history can be a different random draw from
      // another socket; don't let that win once we know the real name.
      const fromProp = aiAgentName && aiAgentName !== "AI Agent" ? aiAgentName : "";
      const activeBotName = fromProp || m.senderName || m.sender_name || "AI Agent";
      const name = capName(activeBotName);
      return `${name} ${time}`;
    }
    return time;
  };

  const [draft, setDraft] = useState("");
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);

  // Built ONCE per customer, when the engineer takes over, and then left alone.
  // It used to rebuild on nearly every click, because the cache was dropped on
  // every new message (see the effect that replaced it) — and in a live chat
  // messages arrive constantly, so `if (aiSummary) return`
  // almost never held. An engineer reads this to find out what they walked
  // into; that does not change as the chat goes on, and watching it re-run on
  // each open was work nobody asked for. Rebuilding is now explicit: the
  // Resummarize link at the foot of the drawer.
  const summaryKey = customerSessionId || sessionId || "";
  const summaryBuiltFor = useRef("");

  const runSummary = async () => {
    setLoadingSummary(true);
    const transcript = normalizeSummaryMessages(messages);
    const localRead = () => buildConversationSummary(transcript, {
      customerName: customerUsername,
    });
    try {
      const res = await authFetch("/api/copilot/summarize-conversation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: transcript,
          session_id: customerSessionId || sessionId || null,
          customer_name: customerUsername || "Customer",
        }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      // An empty body is a failure wearing a 200 -- fall back rather than
      // opening an empty drawer.
      setAiSummary(String(data?.summary || "").trim() || localRead());
    } catch (err) {
      // Never surfaced as an error: the engineer needs SOMETHING to read on a
      // handoff, and the local read is the same four sections without the
      // model's prose. Logged so a broken endpoint is still visible.
      console.error("AI conversation summary failed — falling back to the local read:", err);
      setAiSummary(localRead());
    } finally {
      setLoadingSummary(false);
    }
  };

  // The button in the header is now open/close and nothing else. The first
  // build is the effect below, so by the time the drawer is opened the summary
  // is usually already there.
  const handleFetchSummary = () => {
    if (summaryOpen) {
      setSummaryOpen(false);
      return;
    }
    setSummaryOpen(true);
    if (aiSummary || loadingSummary) return;
    summaryBuiltFor.current = summaryKey;
    runSummary();
  };

  // Explicit rebuild. The only thing that re-runs the model on a conversation
  // that already has a summary.
  const handleResummarize = () => {
    if (loadingSummary) return;
    summaryBuiltFor.current = summaryKey;
    runSummary();
  };

  // The one automatic build, on taking over the conversation.
  //
  // Waits for the transcript to SETTLE rather than firing on the first message.
  // The pane fills in bursts — bind sends the stored history, the greeting
  // arrives separately, a late `tse:load_chat_history` can replace the lot —
  // and summarizing the first burst would describe a conversation consisting of
  // the engineer's own hello. A quiet gap means the transcript is all there.
  //
  // Keyed on the customer, not on a boolean: attending a second escalation on
  // the same panel is a different conversation and gets its own summary, while
  // messages arriving within one conversation never trigger another build.
  useEffect(() => {
    if (!summaryKey || messages.length === 0) return undefined;
    if (summaryBuiltFor.current === summaryKey) return undefined;

    // The transcript settling is now the ONLY thing this build waits for. It
    // used to hold for up to 12s for a sentiment reading, because the meter was
    // baked into the summary text and a build that landed before the score did
    // read "Not yet measured." for the rest of the call. The summary no longer
    // carries sentiment — that is a live widget elsewhere — so there is nothing
    // left to wait for, the panel no longer takes a `sentiment` prop, and this
    // effect no longer re-schedules itself on every score tick.
    const t = setTimeout(() => {
      if (summaryBuiltFor.current === summaryKey) return;
      summaryBuiltFor.current = summaryKey;
      runSummary();
    }, SUMMARY_SETTLE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey, messages.length]);

  // A different customer is a different conversation: drop the previous one's
  // summary immediately rather than showing it under the new transcript.
  useEffect(() => {
    setAiSummary("");
  }, [summaryKey]);

  useEffect(() => {
    if (!aiAgentName || aiAgentName === "AI Agent") return;
    setMessages((prev) => prev.map((m) => {
      if (String(m.sender || "").toLowerCase() !== "bot") return m;
      if (m.sender_name === aiAgentName && m.senderName === aiAgentName) return m;
      return { ...m, sender_name: aiAgentName, senderName: aiAgentName };
    }));
  }, [aiAgentName]);



  useEffect(() => {
    if (!socket || !isConnected) return undefined;
    const sock = socket;
    // Every relayed live-chat event now carries the session it belongs to, so
    // a dashboard that has attended more than one escalation on the same
    // socket cannot render another customer's messages into this transcript.
    // A payload without the field predates it and is accepted.
    const isForThisCustomer = (data) => {
      const from = data?.session_id ? String(data.session_id) : "";
      if (!from || !customerSessionId) return true;
      return from === customerSessionId;
    };
    const onLiveMessage = (data) => {
      if (!isForThisCustomer(data)) return;
      if (data.sender === "system") {
        setMessages((prev) => [...prev, {
          type: "status",
          text: data.text,
          timestamp: data.timestamp || Date.now(),
        }]);
        return;
      }
      if (data.sender === "user" || data.sender === "customer" || data.sender === "agent") {
        setMessages((prev) => {
          // Avoid duplicate agent message if already added locally
          if (data.sender === "agent" && prev.some(m => m.text === data.text && m.sender === "agent")) {
            return prev;
          }
          return [...prev, {
            text: data.text,
            sender: data.sender === "customer" ? "user" : data.sender,
            timestamp: data.timestamp || Date.now(),
            sender_name: data.sender_name || data.agentName || undefined,
          }];
        });
      }
    };
    const onHistory = (historyArr) => {
      if (Array.isArray(historyArr)) {
        const formatted = historyArr.map((msg) => (
          // "Your session is being escalated", "<name> joined the chat", "the
          // live chat ended" — narration, not something the customer said.
          // Without this they arrive as sender "system", fall through to the
          // customer branch and render as the CUSTOMER's own bubbles, so the
          // engineer reads "Your session is being escalated…" as if the
          // customer had typed it. `type: "status"` is the row this panel
          // already draws for notices (d5-customer-status-line).
          msg.sender === "system"
            ? { type: "status", text: msg.text || "", timestamp: msg.timestamp || Date.now() }
            : {
              text: msg.text || "",
              sender: msg.sender === "bot" ? "bot" : msg.sender,
              timestamp: msg.timestamp || Date.now(),
              sources: msg.sources || undefined,
              sender_name: msg.sender_name || msg.agentName || undefined,
            }
        ));
        setMessages(formatted);
      }
    };

    // No joinCopilotRoom and no newCopilotSession listener. `copilot_room` is
    // legacy Dashboard2 wiring that the server joins sockets to and then never
    // emits into, and nothing in the backend has ever sent newCopilotSession —
    // so the `session` state it fed was permanently null, which is what left
    // this composer disabled with "Connect to a session to start chatting…".
    // The session now arrives as a prop from the escalation binding.
    // The customer's connection, as the server sees it. A refresh on their
    // side is a disconnect on ours, so without this the engineer types into a
    // chat nobody is reading and only finds out when it closes.
    const onPeer = (data) => {
      if (!isForThisCustomer(data)) return;
      if (data?.party !== "customer") return;
      if (data.state === "reconnecting") {
        setPeerState({ state: "reconnecting", secondsLeft: data.seconds_left ?? null });
      } else {
        // "reconnected" clears it; "lost" is followed by tse:live_chat_ended,
        // which owns what the panel shows from then on.
        setPeerState(null);
      }
    };

    sock.on("liveAgentMessage", onLiveMessage);
    sock.on("conversationHistory", onHistory);
    sock.on("tse:live_chat_peer", onPeer);
    return () => {
      sock.off("liveAgentMessage", onLiveMessage);
      sock.off("conversationHistory", onHistory);
      sock.off("tse:live_chat_peer", onPeer);
    };
  }, [socket, isConnected, customerSessionId]);

  // 'idle' (nothing bound yet) | 'connecting' (zoom:bind emitted, no reply
  // yet) | 'live' | 'paused' | 'stopped'. Drives both the header subtitle and
  // which of the Join call / pause / play / stop controls show.
  const [callStatus, setCallStatus] = useState("idle");
  // Latest synthesized call notes pushed from the server ({ text, rev }).
  // The send button reads this and nothing else -- the click costs one state
  // read, so it lands in the composer in the same frame.
  const [callFacts, setCallFacts] = useState({ text: "", rev: 0 });

  const pushStatusLine = (text) => {
    setMessages((prev) => [...prev, { type: "status", text, timestamp: Date.now() }]);
  };

  // Live Zoom call transcript. The RTMS bridge now lives inside the main
  // backend (server/app/sockets_zoom.py), but it stays a separate socket
  // connection from useTseSocket's -- different event namespace, different
  // lifecycle. Only ever connects when enableZoomBridge is true
  // (TseDashboard5); a silent no-op for Dashboard5, which never passes it.
  const { bindMeeting: bindZoomMeeting, syncFacts, pauseTranscript, resumeTranscript, stopTranscript } = useZoomBridgeSocket({
    enabled: enableZoomBridge,
    onTranscript: (data) => {
      // speaker_role is "agent" or "customer" when the bridge can tell;
      // unknown falls back to "customer" since that's the side most worth
      // surfacing here.
      setMessages((prev) => [...prev, {
        text: data.text,
        sender: data.speaker_role || "customer",
        speakerName: data.speaker,
        timestamp: data.timestamp || Date.now(),
        source: "zoom",
      }]);
    },
    // Only ever arrives in response to a send click (zoom:sync_facts), so it
    // completes that click rather than updating something in the background.
    onCallFacts: (data) => setCallFacts({ text: data?.text || "", rev: data?.rev || 0 }),
    onBound: () => { setCallStatus("live"); pushStatusLine("Call connected — transcribing live."); },
    // Wired because it was not: the bridge's only failure reply was being
    // dropped on the floor. The server refuses a bind with no session id and
    // says so on zoom:error, and with no handler the panel showed "Connecting…"
    // over a call that would never arrive.
    onError: (data) => {
      // Back to idle, not left on "connecting": the Join call button only
      // renders on idle/stopped, so a stuck "connecting" would hide the one
      // control that could retry the bind.
      setCallStatus((prev) => (prev === "connecting" ? "idle" : prev));
      pushStatusLine(`Call bridge error: ${data?.message || "unknown"}`);
    },
    onPaused: () => { setCallStatus("paused"); pushStatusLine("Transcription paused."); },
    onResumed: () => { setCallStatus("live"); pushStatusLine("Transcription resumed."); },
    onStopped: () => { setCallStatus("stopped"); pushStatusLine("Transcription stopped."); },
  });

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages]);

  // Says WHO closed it, because the two endings are not the same event to the
  // engineer: one they did, one was done to them. The header notice that
  // carries the same news clears itself after 8 seconds, so this line in the
  // transcript is the only durable record of either.
  const chatEndedNotice = liveChatEndedBy === "customer"
    ? "The customer ended the chat — they're back with the AI assistant."
    : "You ended the chat — the customer is back with the AI assistant.";

  useEffect(() => {
    if (!chatEnded) {
      chatEndedStatusAppended.current = false;
      return;
    }
    if (chatEndedStatusAppended.current) return;
    chatEndedStatusAppended.current = true;
    setMessages((prev) => {
      if (prev.some((m) => m.type === "status" && m.text === chatEndedNotice)) return prev;
      return [...prev, { type: "status", text: chatEndedNotice, timestamp: Date.now() }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatEnded, chatEndedNotice]);

  const handleEndChatClick = () => {
    setShowEndChatConfirm(true);
  };

  const handleConfirmEndChat = () => {
    setShowEndChatConfirm(false);
    onEndLiveChat?.();
  };

  /** Post text to the customer: into this transcript and out over the socket.
   *
   * Addressed with `customerSessionId`, which is the escalated customer's id.
   * It used to be `activeSession.user_sid`, and the dashboard filled that in
   * with its OWN session id — so the server looked up the engineer's session,
   * found the engineer's socket in the live-agent seat, and delivered the
   * reply back to the engineer. The customer never heard a word.
   *
   * The `agentMessage` fallback that used to sit on the else branch is gone:
   * the server has no handler for that event, so it silently dropped anything
   * sent outside an escalation. There is nothing to send to outside one.
   */
  const deliver = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return;
    const target = customerSessionId || activeSession?.user_sid;
    if (!target) return;
    setMessages((prev) => [...prev, { text, sender: "agent", timestamp: Date.now() }]);
    socket?.emit("liveAgentMessage", {
      text,
      user_sid: target,
      session_id: target,
      agent_name: agentName,
    });
  };

  // This panel's composer is dead weight in the Zoom flow — `session` never
  // gets set (see the enableZoomBridge branch below), so it would otherwise
  // just sit disabled forever. Repurposed instead: the same send button hands
  // the call's synthesized notes up to TseDashboard5, which drops them into
  // the Copilot composer.
  //
  // The click IS the checkpoint. Pressing send synthesizes everything spoken
  // since the last press — never earlier, so a correction that lands a second
  // before the click is in, and never later, so nothing is summarized while
  // the customer is still mid-thought. The server pops its transcript buffer
  // at that moment, which is what makes the next click start from here.
  const [syncedZoomCount, setSyncedZoomCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  // The sheet is cumulative — every rebuild returns the WHOLE thing — so what
  // goes to the Copilot is the difference against what it already has. A
  // corrected line ("Zcc version: 1.0 (corrected from 2.0)") reads as new
  // text, so corrections travel without restating the call.
  const [sentFactsText, setSentFactsText] = useState("");

  const zoomMessages = messages.filter((m) => m.source === "zoom");
  const hasUnsentTranscript = zoomMessages.length > syncedZoomCount;

  // ── The same mechanism, on the escalated live chat ────────────────────────
  // An escalation runs with the Zoom bridge off (TseDashboard5 passes
  // enableZoomBridge={!escalationActive}), so none of the above fires — but the
  // engineer is in exactly the position the Zoom flow was built for: a
  // conversation is happening beside the Copilot and the Copilot knows nothing
  // about it.
  //
  // So it goes through the SAME condenser, not a local summary. `messages` is
  // deliberately not the source: the fact sheet is built by the model from the
  // transcript the SERVER holds (tse:sync_chat_facts -> update_call_facts ->
  // render_call_facts), which is what makes the output a fact sheet
  // ("Zcc version: Ztunnel 2.0 + DTLS") rather than a narrative. The panel's
  // own copy still rides along, because it holds live-chat messages the store
  // may not have caught up with yet.
  //
  // The reply arrives as tse:call_facts and lands in `callFacts`, so the delta
  // effect above runs unchanged for both flows.
  // Is there actually a CALL behind the Zoom branch? `enableZoomBridge` only
  // says "not escalated", so a dashboard opened with no customer bound sits in
  // the Zoom branch with nothing bound to it — composer read-only, arrow
  // permanently disabled, no way to reach anything. The Zoom send button is
  // only the Zoom send button once a call exists; otherwise the arrow does the
  // chat sync, which resolves the newest conversation server-side.
  const zoomCallActive = enableZoomBridge
    && (callStatus === "live" || callStatus === "paused" || hasUnsentTranscript);

  // The customer being gone outranks everything else the header could say:
  // a call status is irrelevant if the person is not there.
  const subtitle = peerState?.state === "reconnecting"
    ? (peerState.secondsLeft
        ? `Customer reconnecting… ${peerState.secondsLeft}s`
        : "Customer reconnecting…")
    : zoomCallActive
    ? { idle: "Waiting for call to connect", connecting: "Connecting to the call…", live: "On a live call", paused: "Transcription paused", stopped: "Call ended" }[callStatus]
    : callStatus === "connecting"
      ? "Connecting to the call…"
      : (canType || activeSession)
        ? "Live chat with the customer"
        : "";

  const syncChatToCopilot = () => {
    if (syncing || !socket || !customerTarget) return;
    setSyncing(true);
    socket.emit("tse:sync_chat_facts", {
      copilot_session_id: sessionId || "",
      customer_session_id: customerTarget,
      // The live chat is not in the session store — nothing commits a turn once
      // a human takes over, so the server's own read stops at the last AI turn
      // and every click after the first folded nothing. This panel holds the
      // only complete copy, so it sends it. Status rows are dropped; they are
      // UI chrome, not things anybody said.
      messages: messages
        .filter((m) => m.type !== "status" && String(m.text || "").trim())
        .map((m) => ({ sender: m.sender, text: m.text })),
    });
  };

  // Fill the pane on connect, without waiting for an escalation bind.
  //
  // The pane used to fill ONLY from tse:bind_escalation's conversationHistory,
  // so a bind that was refused — or a dashboard opened after a restart, when
  // the customer's session is no longer in the server's live map — left it
  // empty beside a Copilot already holding that conversation as context. This
  // asks for the transcript directly; it is read-only and needs no seat.
  //
  // Skipped once there are messages, so it can never replace a live chat that
  // has content the store does not (see the guard on the server's own
  // conversationHistory emit).
  const historyRequestedRef = useRef("");
  useEffect(() => {
    if (!socket || !isConnected || !sessionId) return;
    // Both ids or nothing. The pane shows the customer this dashboard was
    // opened for (?customer= in its URL); with no id there is no conversation
    // to show, and the server no longer guesses one.
    if (!customerTarget) return;
    if (messages.length > 0) return;
    const key = `${socket.id || ""}:${customerTarget}`;
    if (historyRequestedRef.current === key) return;
    historyRequestedRef.current = key;
    socket.emit("tse:load_chat_history", {
      copilot_session_id: sessionId,
      customer_session_id: customerTarget,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, sessionId, customerTarget, messages.length]);

  // Completes the round trip. `reason` means the server had nothing to fold —
  // no transcript anywhere, or nothing new since the last click — which is a
  // no-op, not an error, so the button just unlocks.
  useEffect(() => {
    if (!socket || !isConnected) return undefined;
    const onChatFacts = (payload) => {
      const text = String(payload?.text || "").trim();
      if (!text) {
        setSyncing(false);
        return;
      }
      setCallFacts((prev) => ({ text, rev: prev.rev + 1 }));
    };
    socket.on("tse:call_facts", onChatFacts);
    return () => socket.off("tse:call_facts", onChatFacts);
  }, [socket, isConnected]);

  // Completes the click: the synthesized sheet has arrived, so hand the
  // Copilot only the part it hasn't seen.
  useEffect(() => {
    if (!syncing || !callFacts.text) return;
    const sent = new Set(sentFactsText.split("\n").map((l) => l.trim()).filter(Boolean));
    const additions = callFacts.text.split("\n").map((l) => l.trim()).filter((l) => l && !sent.has(l));
    if (additions.length) onRequestDiagnosis?.(additions.join("\n"));
    setSentFactsText(callFacts.text);
    setSyncing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callFacts.rev, callFacts.text]);

  const sendMessage = () => {
    if (zoomCallActive) {
      if (!hasUnsentTranscript || syncing) return;
      // Checkpoint here, then summarize from here. Everything spoken up to
      // this instant is in; the next click starts after it.
      setSyncedZoomCount(zoomMessages.length);
      setSyncing(true);
      syncFacts(sessionId);
      return;
    }
    // canType, not canChat: `deliver` needs a customer to send to, which is
    // what canType checks. Requiring the escalation flag here would make the
    // composer typeable but inert whenever a bind was refused — worse than
    // being disabled, because it silently swallows what the engineer typed.
    if (!draft.trim() || !canType) return;
    deliver(draft);
    setDraft("");
  };

  // The copilot's per-message actions act on THIS panel — "Send to customer"
  // and "Edit in customer chat" both target the left side, as in Dashboard2.
  // The panel owns its transcript and composer, so it exposes them rather than
  // lifting that state into the parent.
  useImperativeHandle(ref, () => ({
    sendToCustomer: deliver,
    fillComposer: (raw) => {
      setDraft(String(raw || ""));
      // Height is inline, so it has to be recomputed for the pasted text.
      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
        ta.focus();
      }, 0);
    },
    // TseDashboard5's "Connect this call" modal calls this after the agent
    // pastes the meeting link — this panel owns the bridge connection, so it
    // owns the bind too, same reasoning as sendToCustomer/fillComposer above.
    // `explicitSessionId` wins when the caller passes one. It has the ref,
    // which is accurate in the tick the session is minted; this component has
    // the prop, which is a render behind at exactly that moment.
    bindZoomMeeting: (meetingRef, explicitSessionId) => {
      // The only place "connecting" is entered. onBound flips it to "live",
      // onError puts it back to "idle" — so the word is never on screen
      // without an outstanding zoom:bind behind it.
      setCallStatus("connecting");
      bindZoomMeeting(explicitSessionId || sessionId, meetingRef, agentName);
    },
  }));

  return (
    <div className={`d5-customer-panel ${embedded ? "d5-customer-panel-embedded" : ""}`}>
      <div className="d5-panel-header">
        <div className="d5-panel-header-left">
          <div className="d5-panel-icon d5-panel-icon-customer">
            <MessageCircle size={15} />
          </div>
          <div>
            <h2 className="d5-panel-title">Customer Chat / Call</h2>
            {/* "Waiting for call to connect" only while a call is actually
                expected. It used to show whenever the panel was not
                escalated, which described a call that was never coming and
                hid the fact that the conversation below is a chat.

                "Connecting…" is now bound to one thing only: a zoom:bind that
                has been emitted and not yet answered, i.e. the agent clicked
                Join call. It used to be the default subtitle for a panel with
                no session at all, which described a connection nobody asked
                for and that nothing could ever finish — isConnected never
                reached this line. With nothing true to say the subtitle is not
                rendered; the body already states that no customer is
                attached. */}
            {subtitle && <p className="d5-panel-subtitle">{subtitle}</p>}
          </div>
        </div>
        {/* Always here, in every call state — the context the agent carries is
            the one thing the engineer must be able to read and correct at any
            moment, and in the thread it scrolls away. Shows its own size so a
            silently-empty context is visible without opening anything.

            Not gated on enableZoomBridge any more. This is one component
            serving both surfaces, and an escalated chat now feeds the Copilot
            through the same condenser a call does — so the engineer has exactly
            the same context to read and correct, and had no way to reach it.
            GlobalContextPanel and these props are already rendered for both
            (TseDashboard5 passes them unconditionally). */}
        <button
          type="button"
          className={`d5-customer-ctx-btn ${contextOpen ? "d5-customer-ctx-btn-on" : ""} ${contextCount ? "" : "d5-customer-ctx-btn-empty"}`}
          aria-label="Context"
          data-label="Context"
          title={contextCount
            ? `Context the Copilot is carrying — ${contextCount} block${contextCount > 1 ? "s" : ""}, ${contextChars} chars. Click to read or edit.`
            : "No context attached yet — press send to hand the Copilot what has been said"}
          onClick={onToggleContext}
        >
          <Brain size={16} />
        </button>
        {enableZoomBridge && (callStatus === "idle" || callStatus === "stopped") && (
          <button type="button" className="d5-customer-join-call-btn" aria-label="Join call" data-label="Join call" onClick={onJoinCall}>
            <Video size={16} />
          </button>
        )}
        {enableZoomBridge && (callStatus === "live" || callStatus === "paused") && (
          <div className="d5-customer-call-controls">
            {callStatus === "live" ? (
              <button type="button" className="d5-customer-call-btn" title="Pause transcription" onClick={() => pauseTranscript(sessionId)}>
                <Pause size={14} />
              </button>
            ) : (
              <button type="button" className="d5-customer-call-btn" title="Resume transcription" onClick={() => resumeTranscript(sessionId)}>
                <Play size={14} />
              </button>
            )}
            <button type="button" className="d5-customer-call-btn d5-customer-call-btn-stop" title="Stop transcription" onClick={() => stopTranscript(sessionId)}>
              <Square size={13} />
            </button>
          </div>
        )}
        {canChat && onEndLiveChat && (
          <button
            type="button"
            className="d5-customer-end-chat-btn"
            onClick={handleEndChatClick}
            title="End live chat with customer"
          >
            {/* Same Square the Zoom transcription stop uses, so "stop" reads
                the same way on both surfaces. */}
            <Square size={11} fill="currentColor" />
            End chat
          </button>
        )}
        {messages.length > 0 && (
          <button
            type="button"
            className={`d5-customer-ctx-btn ${summaryOpen ? "d5-customer-ctx-btn-on" : ""}`}
            title="AI Conversation Summary"
            onClick={handleFetchSummary}
            aria-label="AI Summary"
            data-label="AI Summary"
            style={{ marginRight: "4px" }}
          >
            <Sparkles size={15} />
          </button>
        )}
        {onCollapse && (
          <button

            type="button"
            className="d5-collapse-button"
            onClick={onCollapse}
            title="Collapse panel"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              color: "var(--arta-text-muted)"
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
      </div>

      {summaryOpen && (
        /* The call-context drawer's own shell (.d5-gctx), reused rather than
           re-invented: absolute over the transcript from under the header,
           its own head and scrolling body, the same fade-up. The summary used
           to sit in the FLOW, so opening it pushed the whole conversation
           down and the engineer lost their place. */
        <div className="d5-gctx" role="dialog" aria-label="AI conversation summary">
          <div className="d5-gctx-head">
            <span className="d5-gctx-title">
              <Sparkles size={11} /> AI conversation summary
              {(typeof sentiment === "number" || typeof prevSentiment === "number") && (
                <span className="d5-gctx-count">
                  {`sentiment ${(typeof sentiment === "number" ? sentiment : prevSentiment).toFixed(1)}/10`}
                </span>
              )}
            </span>
            <button
              type="button"
              className="d5-gctx-x"
              title="Close"
              onClick={() => setSummaryOpen(false)}
            >
              <X size={14} />
            </button>
          </div>
          <div className="d5-gctx-body">
          {loadingSummary ? (
            <div style={{ color: "var(--arta-text-muted, #666)", fontStyle: "italic" }}>Summarizing conversation...</div>
          ) : (
            <div
              className="d5-summary-content"
              dangerouslySetInnerHTML={{ __html: summaryHtml(aiSummary) }}
            />
          )}
          {/* Bottom right, and deliberately quiet: the same 11px muted grey as
              the panel subtitle ("Live chat with the customer"), so it reads as
              an action available rather than one being urged. Underlined
              because with no border or background there is nothing else to say
              it is clickable. */}
          {/* Sentiment, appended to the summary rather than living only behind
              the avatar menu: the summary is what the engineer reads when they
              pick up an escalation, and how the customer feels is part of that
              handoff. Same labels and /10 scale as the profile menu, so the two
              can never read as different measures. Hidden when neither number
              exists - an empty gauge says nothing and costs a line. */}
          {!loadingSummary && aiSummary
            && (typeof sentiment === "number" || typeof prevSentiment === "number") && (
            <div style={{
              marginTop: "10px",
              paddingTop: "8px",
              borderTop: "1px solid var(--arta-border, #eee)",
              fontSize: "12px",
              color: "var(--arta-text-muted, #666)",
            }}>
              <strong style={{ fontWeight: 600, color: "var(--arta-text-main, #111)" }}>Sentiment meter</strong>
              <div style={{ marginTop: "4px" }}>
                <strong style={{ fontWeight: 600, color: "var(--arta-text-main, #111)" }}>
                  {typeof sentiment === "number" ? sentiment.toFixed(1) : "-"}/10
                </strong>
              </div>
            </div>
          )}
          {aiSummary && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
              <button
                type="button"
                onClick={handleResummarize}
                disabled={loadingSummary}
                title="Rebuild this summary from the conversation as it stands now"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: loadingSummary ? "default" : "pointer",
                  fontSize: "11px",
                  color: "var(--arta-text-muted)",
                  textDecoration: "underline",
                }}
              >
                {loadingSummary ? "Resummarizing…" : "Resummarize"}
              </button>
            </div>
          )}
          </div>
        </div>
      )}

      <div ref={bodyRef} className="d5-customer-body" onClick={(e) => {
        const imgLink = e.target.closest("a.image-link");
        if (imgLink) { e.preventDefault(); const u = imgLink.getAttribute("data-image-url"); if (u) setZoomImageUrl(u); return; }
        const docLink = e.target.closest("a.citation-link");
        if (docLink) { e.preventDefault(); const du = docLink.getAttribute("data-doc-url"); if (du) openDocument(du); }
      }}>
        {messages.length === 0 ? (
          <div className="d5-customer-empty">
            <div className="d5-customer-empty-icon">
              <MessageCircle size={18} />
            </div>
            <span>
              {activeSession?.escalationActive
                ? "Connected. The customer's earlier messages will appear here."
                : zoomCallActive
                  ? "Waiting for call to connect"
                  : customerTarget
                    /* The conversation loads on connect from the ?customer= id.
                       Seeing this means it has not arrived yet, or there is
                       nothing stored for that customer. */
                    ? "Loading this customer's conversation…"
                    : "No customer attached to this session"}
            </span>
          </div>
        ) : (
          messages.map((m, i) => (
            m.type === "status" ? (
              <div
                key={i}
                className={`d5-customer-status-line${m.info === false ? "" : " is-info"}`}
              >{m.text}</div>
            ) : (
              <div key={i} className={`d5-customer-msg d5-customer-msg-${m.sender}`}>
                {m.source === "zoom" && (
                  <span className="d5-customer-zoom-tag" title="From live Zoom call transcript">
                    <Mic size={10} /> {m.speakerName || (m.sender === "agent" ? "Agent" : "Customer")}
                  </span>
                )}
                <div className="d5-customer-bubble" dangerouslySetInnerHTML={{ __html: formatBotMessage(m.text, m.sources) }} />
                <span className="d5-customer-timestamp d5-customer-timestamp-visible">{messageReceipt(m)}</span>
              </div>
            )
          ))
        )}
      </div>
      <div className="d5-customer-composer">
        <div className="d5-customer-composer-row">
          <textarea
            className="d5-customer-input"
            placeholder={
              syncing
                ? "Summarizing the conversation…"
                : zoomCallActive
                  ? (hasUnsentTranscript
                    ? (sentFactsText ? "Click send to add what's new to the Copilot's context" : "Click send to give the Copilot this call as context")
                    : (sentFactsText ? "Waiting for new details from the call…" : "Listening to the call…"))
                  /* "Enter to send" is spelled out because the arrow beside it no
                     longer sends the reply — it hands the conversation to the
                     Copilot, the way it does on a Zoom call. */
                  : canType
                    ? "Reply to customer (Enter to send)…"
                    : chatEnded
                      ? (liveChatEndedBy === "customer" ? "Chat ended by the customer" : "You ended this chat")
                      : "No customer attached to this session"
            }
            value={draft}
            /* All three gated on zoomCallActive, not enableZoomBridge. The
               Zoom branch is read-only because a live transcript is not
               something you type into — but "not escalated" is not the same as
               "on a call", and keying these off enableZoomBridge left the box
               dead whenever no call was bound. */
            disabled={zoomCallActive ? (!hasUnsentTranscript || syncing) : !canType}
            readOnly={zoomCallActive}
            ref={textareaRef}
            rows={1}
            onChange={(e) => {
              if (zoomCallActive) return;
              setDraft(e.target.value);
              const ta = textareaRef.current;
              if (ta) { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 120) + "px"; }
            }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          />
          {/* Send to the CUSTOMER. The arrow to its right hands the
              conversation to the Copilot as context; this one is the ordinary
              reply, and it exists because that was the arrow's old job and
              Enter alone is not a discoverable replacement for a button people
              were already clicking.
              Hidden on a live Zoom call: there is no chat to reply into. */}
          {!zoomCallActive && (
            <button
              type="button"
              className="d5-customer-send d5-customer-send-reply"
              disabled={!canType || !draft.trim()}
              title={canType ? "Send to customer" : "No customer attached to this session"}
              aria-label="Send to customer"
              onClick={() => { if (!draft.trim() || !canType) return; deliver(draft); setDraft(""); }}
            >
              <ArrowUp size={15} />
            </button>
          )}
          <button
            type="button"
            className="d5-customer-send"
            /* Gated on having a CUSTOMER, not on `canChat` or on this panel
               holding messages: the transcript may live only in the store (the
               pane is still loading, or the bind was refused), and the server
               reads it by id. With no customer there is nothing to summarize. */
            disabled={zoomCallActive ? (!hasUnsentTranscript || syncing) : (syncing || !customerTarget)}
            title={
              !zoomCallActive && !customerTarget
                ? "No customer attached to this session"
                : sentFactsText
                  ? "Summarize and send what's been said since the last time"
                  : (zoomCallActive
                    ? "Send this call to the Copilot as context"
                    : "Send this conversation to the Copilot as context")
            }
            onClick={zoomCallActive ? sendMessage : syncChatToCopilot}
          >
            <Send size={15} />
          </button>
        </div>
      </div>
      {zoomImageUrl && <ImagePreviewModal imageUrl={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />}
      {showEndChatConfirm && (
        <EndChatConfirmModal
          onCancel={() => setShowEndChatConfirm(false)}
          onConfirm={handleConfirmEndChat}
        />
      )}
    </div>
  );
});

CustomerChatPanel.displayName = "CustomerChatPanel";

export default CustomerChatPanel;
